import React, { useEffect, useState } from "react";
import {
  Target,
  X,
  Loader2,
  CheckCircle2,
  Mail,
  UserPlus,
  Copy,
  History,
  Check,
  Award,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { getAPIRoot, getCachedAccessToken } from "../../services/api";
import { useToast } from "../shared/ToastNotification";

export default function InviteFriendsModal({ open, onClose }) {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const API = getAPIRoot();

  const [activeTab, setActiveTab] = useState("send"); // "send" or "rewards"
  const [credits, setCredits] = useState(null);
  const [frozenUntil, setFrozenUntil] = useState(null);
  const [frozenReason, setFrozenReason] = useState(null);
  const [buying, setBuying] = useState(false);
  const [nextMilestone, setNextMilestone] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState("");

  // Rewards & History states
  const [myInviteCode, setMyInviteCode] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Fetch credits when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCredits(true);
    const tok = user?.access_token || getCachedAccessToken();
    const h = tok ? { Authorization: `Bearer ${tok}` } : {};
    fetch(`${API}/invite/my-code`, { credentials: "include", headers: h })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d) {
          setCredits({
            remaining: d.credits_remaining ?? 0,
            total: d.credits_total ?? 3,
            used: d.credits_used ?? 0,
          });
          setFrozenUntil(d.frozen_until || null);
          setFrozenReason(d.frozen_reason || null);
          setMyInviteCode(d.code || "");
          if (d.milestones) setMilestones(d.milestones);
          if (d.next_milestone) setNextMilestone(d.next_milestone);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingCredits(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, API, user?.access_token]);

  // Fetch referrals and rewards when rewards tab is opened
  useEffect(() => {
    if (!open || activeTab !== "rewards") return;
    let cancelled = false;

    setLoadingReferrals(true);
    setLoadingRewards(true);
    const tok = user?.access_token || getCachedAccessToken();
    const h = tok ? { Authorization: `Bearer ${tok}` } : {};

    // 1. Fetch Referral Conversions
    fetch(`${API}/invite/my-referrals`, { credentials: "include", headers: h })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data?.referrals) {
          setReferrals(data.referrals);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingReferrals(false);
      });

    // 2. Fetch Points Rewards
    fetch(`${API}/invite/my-rewards`, { credentials: "include", headers: h })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data?.rewards) {
          setRewards(data.rewards);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingRewards(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, activeTab, API, user?.access_token]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setEmail("");
      setName("");
      setSending(false);
      setSent(false);
      setSentTo("");
      setNextMilestone(null);
      setMilestones(null);
      setActiveTab("send");
      setCopiedCode(false);
    }
  }, [open]);

  const handleSend = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      showError("Enter a valid email address.");
      return;
    }
    if (!credits || credits.remaining <= 0) {
      showError("No invitations remaining.");
      return;
    }
    setSending(true);
    try {
      const tok = user?.access_token || getCachedAccessToken();
      const res = await fetch(`${API}/invite/issue`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
        body: JSON.stringify({
          recipient_email: trimmed,
          recipient_name: name.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data.detail || "Could not send invitation.");
        return;
      }
      setSent(true);
      setSentTo(trimmed);
      setCredits((prev) =>
        prev
          ? {
              ...prev,
              remaining: data.credits_remaining ?? prev.remaining - 1,
              used: prev.used + 1,
            }
          : prev,
      );
      showSuccess(`Invitation sent to ${trimmed}`);
    } catch (err) {
      showError(err.message || "Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = async () => {
    if (!myInviteCode) return;
    const shareUrl = `${window.location.origin}/invite/${myInviteCode}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedCode(true);
      showSuccess("Invite link copied to clipboard!");
      setTimeout(() => setCopiedCode(false), 2500);
    } catch (_) {
      showError("Could not copy link.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !sending && email.trim()) {
      handleSend();
    }
    if (e.key === "Escape") {
      onClose();
    }
  };

  if (!open) return null;

  const remaining = credits?.remaining ?? 0;
  const total = credits?.total ?? 3;

  // Compute status badge color and text
  const getReferralBadge = (ref) => {
    if (ref.status !== "converted") {
      return (
        <span className="rounded-md bg-white/[0.04] border border-white/[0.06] px-1.5 py-0.5 text-[10px] text-white/45">
          Invited
        </span>
      );
    }
    switch (ref.reward_status) {
      case "awarded":
        return (
          <span className="rounded-md bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 text-[10px] font-medium text-emerald-400">
            Points Awarded
          </span>
        );
      case "eligible":
        return (
          <span className="rounded-md bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-400 animate-pulse">
            Eligible
          </span>
        );
      case "rejected":
        return (
          <span className="rounded-md bg-rose-500/10 border border-rose-500/20 px-1.5 py-0.5 text-[10px] font-medium text-rose-400">
            Rejected
          </span>
        );
      default:
        return (
          <span className="rounded-md bg-[#f9736b]/10 border border-[#f9736b]/20 px-1.5 py-0.5 text-[10px] font-medium text-[#f9736b]">
            Joined (Pending action)
          </span>
        );
    }
  };

  const totalPointsEarned = rewards.reduce((sum, r) => sum + r.points, 0);

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={onClose} />

      <div className="relative w-full overflow-hidden rounded-t-[28px] border border-white/[0.08] bg-[radial-gradient(circle_at_top_left,_rgba(249,115,107,0.18),_transparent_34%),linear-gradient(180deg,#101012_0%,#070708_100%)] shadow-[0_28px_80px_rgba(0,0,0,0.55)] animate-in slide-in-from-bottom-4 fade-in duration-300 sm:max-w-[400px] sm:rounded-[28px]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#f9736b]/10 border border-[#f9736b]/20">
              <UserPlus className="h-4 w-4 text-[#f9736b]" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white/95">Invite Friends</h2>
              <p className="text-[11px] text-white/40">STAGSTER grows through trusted invites.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-white/40 hover:text-white/70 transition-colors"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="mx-5 mb-4 flex rounded-xl bg-white/[0.04] p-1 border border-white/[0.05]">
          <button
            type="button"
            onClick={() => setActiveTab("send")}
            className={`flex-1 rounded-lg py-1.5 text-center text-[12px] font-medium transition-all ${
              activeTab === "send"
                ? "bg-white/[0.08] text-white shadow-sm border border-white/[0.05]"
                : "text-white/40 hover:text-white/60"
            }`}
          >
            Send Invite
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("rewards")}
            className={`flex-1 rounded-lg py-1.5 text-center text-[12px] font-medium transition-all ${
              activeTab === "rewards"
                ? "bg-white/[0.08] text-white shadow-sm border border-white/[0.05]"
                : "text-white/40 hover:text-white/60"
            }`}
          >
            Rewards & Activity
          </button>
        </div>

        {activeTab === "send" ? (
          <>
            {/* Invite available bar */}
            <div className="mx-5 mb-4 rounded-2xl border border-[#f9736b]/15 bg-[#f9736b]/[0.04] px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium text-white/70">Invitations available</span>
                {loadingCredits ? (
                  <Loader2 className="h-4 w-4 animate-spin text-white/40" />
                ) : (
                  <span className="text-[15px] font-bold text-[#f9736b]">
                    {remaining}/{total}
                  </span>
                )}
              </div>
              <div className="flex gap-1.5 mt-2">
                {Array.from({ length: total }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                      i < remaining ? "bg-[#f9736b]" : "bg-white/[0.08]"
                    }`}
                  />
                ))}
              </div>
              {remaining <= 0 && !nextMilestone && (
                <p className="mt-2 text-[11px] text-white/40">
                  No invitations left. Stay active and we will unlock more.
                </p>
              )}
              {frozenUntil ? (
                <p className="mt-2 text-[11px] font-semibold text-rose-300">
                  Inviting is paused until {new Date(frozenUntil).toLocaleDateString("en-IN")}{" "}
                  {frozenReason ? `(${frozenReason})` : ""}
                </p>
              ) : null}
              {remaining <= 0 && !frozenUntil ? (
                <button
                  type="button"
                  disabled={buying}
                  onClick={async () => {
                    setBuying(true);
                    try {
                      const tok = user?.access_token || getCachedAccessToken();
                      const res = await fetch(`${API}/points/redeem`, {
                        method: "POST",
                        credentials: "include",
                        headers: {
                          "Content-Type": "application/json",
                          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
                        },
                        body: JSON.stringify({ reward_key: "invite_credit" }),
                      });
                      const body = await res.json().catch(() => null);
                      if (res.ok) {
                        setCredits((prev) =>
                          prev ? { ...prev, remaining: prev.remaining + 1, total: prev.total + 1 } : prev,
                        );
                        showSuccess("Extra invite unlocked. 500 Stag Points spent.");
                      } else {
                        showError(body?.detail || "Could not buy an extra invite.");
                      }
                    } catch (e) {
                      showError("Could not buy an extra invite.");
                    } finally {
                      setBuying(false);
                    }
                  }}
                  className="mt-2 w-full rounded-xl border border-white/[0.12] bg-white/[0.05] py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-white/70 transition hover:bg-white/[0.09] hover:text-white disabled:opacity-50"
                >
                  {buying ? "Unlocking..." : "Buy extra invite (500 pts)"}
                </button>
              ) : null}
              {nextMilestone && (
                <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#f9736b]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] text-white/60 leading-snug">{nextMilestone.hint}</p>
                      <p className="text-[10px] text-white/30 mt-0.5">
                        +{nextMilestone.credits} invitation
                        {nextMilestone.credits > 1 ? "s" : ""} unlocked
                      </p>
                    </div>
                  </div>
                  {milestones && (
                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/[0.05]">
                      {Object.entries(milestones).map(([key, done]) => (
                        <div
                          key={key}
                          className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                            done ? "bg-emerald-400/70" : "bg-white/[0.08]"
                          }`}
                          title={done ? key.replace(/_/g, " ") : key.replace(/_/g, " ")}
                        />
                      ))}
                      <span className="text-[10px] text-white/25 ml-1 whitespace-nowrap">
                        {Object.values(milestones).filter(Boolean).length}/
                        {Object.keys(milestones).length}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {sent ? (
              <div className="px-5 pb-6">
                <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-5 text-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-[14px] font-semibold text-white/90">Invitation sent!</p>
                  <p className="text-[12px] text-white/50 mt-1">
                    We sent an email to {sentTo} with a link to join.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSent(false);
                    setEmail("");
                    setName("");
                  }}
                  className="mt-3 w-full rounded-2xl border border-white/[0.1] bg-white/[0.04] py-3 text-[13px] font-semibold text-white/70 transition hover:bg-white/[0.07]"
                  type="button"
                >
                  Invite another friend
                </button>
                <button
                  onClick={onClose}
                  className="mt-2 w-full py-2.5 text-[12px] font-medium text-white/40 transition hover:text-white/60"
                  type="button"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="px-5 pb-6 space-y-3">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.12em] text-white/45">
                    Friend's email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30 pointer-events-none" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="friend@example.com"
                      disabled={remaining <= 0}
                      className="w-full rounded-2xl border border-white/[0.1] bg-white/[0.05] pl-10 pr-4 py-3 text-[16px] sm:text-[14px] text-white outline-none placeholder:text-white/25 transition focus:border-[#f9736b]/40 focus:bg-white/[0.07] disabled:opacity-40"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.12em] text-white/45">
                    Name <span className="font-normal normal-case text-white/30">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="What should we call them?"
                    disabled={remaining <= 0}
                    className="w-full rounded-2xl border border-white/[0.1] bg-white/[0.05] px-4 py-3 text-[16px] sm:text-[14px] text-white outline-none placeholder:text-white/25 transition focus:border-[#f9736b]/40 focus:bg-white/[0.07] disabled:opacity-40"
                    style={{ fontSize: "16px" }}
                  />
                </div>
                <button
                  onClick={handleSend}
                  disabled={sending || !email.trim() || remaining <= 0}
                  className={`w-full rounded-2xl py-3.5 text-[14px] font-semibold transition active:scale-[0.98] disabled:active:scale-100 ${
                    remaining > 0
                      ? "bg-[#f9736b] text-black hover:bg-[#fb8a83] disabled:opacity-50"
                      : "bg-white/[0.08] text-white/30"
                  }`}
                  type="button"
                >
                  {sending ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                    </span>
                  ) : remaining > 0 ? (
                    "Send Invitation"
                  ) : (
                    "No invitations left"
                  )}
                </button>
                <p className="text-center text-[11px] text-white/30 pt-1">
                  They will receive an email with a link to join STAGSTER.
                </p>
              </div>
            )}
          </>
        ) : (
          /* Rewards & Activity Tab */
          <div className="px-5 pb-6 space-y-4 max-h-[460px] overflow-y-auto custom-scrollbar">
            {/* Share link block */}
            {myInviteCode && (
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 text-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/40">
                  Your Personal Invite Link
                </p>
                <div className="mt-2.5 flex items-center justify-between rounded-xl border border-white/[0.08] bg-black/40 p-1.5 pl-3.5">
                  <span className="font-mono text-[13px] text-white/70 select-all truncate max-w-[200px]">
                    {myInviteCode}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="flex h-9 items-center gap-1.5 rounded-lg bg-[#f9736b]/10 border border-[#f9736b]/15 px-3.5 text-[12px] font-medium text-[#f9736b] hover:bg-[#f9736b]/15 active:scale-95 transition-all"
                  >
                    {copiedCode ? (
                      <>
                        <Check className="h-3.5 w-3.5" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" /> Copy Link
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Stats Overview */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3 text-center">
                <span className="text-[10px] font-medium text-white/35">Total Invited</span>
                <p className="mt-1 text-[20px] font-bold text-white/90">
                  {loadingReferrals ? (
                    <Loader2 className="h-5 w-5 animate-spin mx-auto text-white/30" />
                  ) : (
                    referrals.length
                  )}
                </p>
              </div>
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3 text-center">
                <span className="text-[10px] font-medium text-white/35">Stag Points Earned</span>
                <p className="mt-1 text-[20px] font-bold text-emerald-400 flex items-center justify-center gap-1">
                  {loadingRewards ? (
                    <Loader2 className="h-5 w-5 animate-spin text-white/30" />
                  ) : (
                    <>
                      <Award className="h-4 w-4 shrink-0" />
                      {totalPointsEarned}
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Invite activity feed */}
            <div className="space-y-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/40 flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" /> Invite History
              </h3>

              {loadingReferrals ? (
                <div className="py-6 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#f9736b]" />
                </div>
              ) : referrals.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 p-6 text-center text-[12px] text-white/30">
                  No invites sent yet. Start inviting friends to grow the network!
                </div>
              ) : (
                <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                  {referrals.map((ref) => (
                    <div
                      key={ref.id}
                      className="flex items-center justify-between rounded-xl border border-white/[0.05] bg-white/[0.01] px-3.5 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium text-white/80 truncate">
                          {ref.referred_name || ref.referred_username || ref.referred_email || "Pending user"}
                        </p>
                        <p className="text-[10px] text-white/35 mt-0.5">
                          Joined{" "}
                          {new Date(ref.converted_at || ref.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                          })}
                        </p>
                      </div>
                      <div className="shrink-0">{getReferralBadge(ref)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reward Points feed */}
            <div className="space-y-2 pt-1">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/40 flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-emerald-400" /> Reward Log
              </h3>

              {loadingRewards ? (
                <div className="py-4 text-center">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto text-emerald-400" />
                </div>
              ) : rewards.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 p-5 text-center text-[12px] text-white/30">
                  No points rewards earned yet. Points are paid out once friends complete their first bill upload!
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[130px] overflow-y-auto pr-1">
                  {rewards.map((rew) => (
                    <div
                      key={rew.id}
                      className="flex items-center justify-between rounded-lg bg-emerald-500/[0.02] border border-emerald-500/10 px-3.5 py-2"
                    >
                      <div>
                        <p className="text-[12px] font-medium text-white/85">Referral Completed</p>
                        <p className="text-[10px] text-white/35">
                          {new Date(rew.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      <span className="text-[13px] font-bold text-emerald-400">+{rew.points} Pts</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}